import React, { RefObject } from 'react';
import { Flex, Spin, Typography, theme } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ChatMessage } from '../core/support-widget-core';
import { AttachmentGallery } from './AttachmentGallery';
import './widget-messenger.css';

dayjs.extend(relativeTime);

/** Consecutive same-sender messages within this window are grouped. */
export const GROUP_WINDOW_MS = 5 * 60 * 1000;

export type HistoryState = 'idle' | 'loading' | 'error';

export interface MessageThreadProps {
    threadRef: RefObject<HTMLDivElement>;
    historyState: HistoryState;
    conversationStarted: boolean;
    messages: ChatMessage[];
    agentTyping: boolean;
    isError: boolean;
    translate: (key: string) => string;
    getAttachmentDownloadUrl: (
        attachmentId: number,
    ) => Promise<{ ok: true; downloadUrl: string } | { ok: false; error: string }>;
}

function formatDateDivider(
    date: string,
    translate: (key: string) => string,
): string {
    const d = dayjs(date);
    const today = dayjs().startOf('day');
    const yesterday = today.subtract(1, 'day');
    if (d.isSame(today, 'day')) {
        return translate('support.help.chat.today');
    }
    if (d.isSame(yesterday, 'day')) {
        return translate('support.help.chat.yesterday');
    }
    return d.format('D MMM YYYY');
}

function formatRelativeTime(
    date: string,
    translate: (key: string) => string,
): string {
    const d = dayjs(date);
    if (dayjs().diff(d, 'minute') < 1) {
        return translate('support.help.chat.justNow');
    }
    return d.fromNow();
}

function bubbleRadius(isOutgoing: boolean): string {
    return isOutgoing ? '12px 12px 4px 12px' : '12px 12px 12px 4px';
}

/**
 * Scrollable message thread with grouping, date dividers, and read receipts.
 */
export const MessageThread: React.FC<MessageThreadProps> = ({
    threadRef,
    historyState,
    conversationStarted,
    messages,
    agentTyping,
    isError,
    translate,
    getAttachmentDownloadUrl,
}) => {
    const { token } = theme.useToken();

    return (
        <div
            ref={threadRef}
            className="support-messenger-thread"
            style={{ background: token.colorBgContainer }}
        >
            {historyState === 'loading' ? (
                <Flex vertical align="center" justify="center" gap="small" style={{ height: '100%' }}>
                    <Spin />
                    <Typography.Text type="secondary">
                        {translate('support.help.chat.loadingHistory')}
                    </Typography.Text>
                </Flex>
            ) : historyState === 'error' ? (
                <Typography.Text type="danger">
                    {translate('support.help.chat.historyFailed')}
                </Typography.Text>
            ) : !conversationStarted ? (
                <>
                    <Typography.Title level={5} style={{ marginTop: 0 }}>
                        {translate('support.help.chat.emptyHeading')}
                    </Typography.Title>
                    <Typography.Paragraph type="secondary">
                        {translate('support.help.chat.emptyBody')}
                    </Typography.Paragraph>
                    <Typography.Text
                        type="secondary"
                        style={{ display: 'block', marginTop: 8, fontSize: 12 }}
                    >
                        {translate('support.help.privacy')}
                    </Typography.Text>
                </>
            ) : messages.length === 0 ? (
                <Typography.Text type="secondary">
                    {translate('support.help.chat.emptyBody')}
                </Typography.Text>
            ) : (
                messages.map((msg, i) => {
                    const prev = messages[i - 1];
                    const isOutgoing = msg.participant.authorType === 'customer';
                    const isFirstInGroup =
                        !prev ||
                        prev.participant.id !== msg.participant.id ||
                        new Date(msg.createdAt).getTime() -
                            new Date(prev.createdAt).getTime() >=
                            GROUP_WINDOW_MS;
                    const isLastInGroup =
                        i === messages.length - 1 ||
                        messages[i + 1].participant.id !== msg.participant.id ||
                        new Date(messages[i + 1].createdAt).getTime() -
                            new Date(msg.createdAt).getTime() >=
                            GROUP_WINDOW_MS;
                    const showDateDivider =
                        !prev ||
                        !dayjs(msg.createdAt).isSame(dayjs(prev.createdAt), 'day');

                    return (
                        <React.Fragment key={msg.id}>
                            {showDateDivider ? (
                                <div className="support-messenger-date-divider">
                                    <span
                                        className="support-messenger-date-divider-label"
                                        style={{
                                            color: token.colorTextSecondary,
                                            background: token.colorBgElevated,
                                        }}
                                    >
                                        {formatDateDivider(msg.createdAt, translate)}
                                    </span>
                                </div>
                            ) : null}
                            <Flex
                                vertical
                                align={isOutgoing ? 'flex-end' : 'flex-start'}
                                style={{ marginBottom: isLastInGroup ? 16 : 8 }}
                            >
                                {msg.content ? (
                                    <div
                                        style={{
                                            maxWidth: '85%',
                                            padding: '10px 14px',
                                            borderRadius: bubbleRadius(isOutgoing),
                                            background: isOutgoing
                                                ? token.colorPrimary
                                                : token.colorBgElevated,
                                            border: isOutgoing
                                                ? 'none'
                                                : `1px solid ${token.colorBorderSecondary}`,
                                            color: isOutgoing
                                                ? token.colorTextLightSolid
                                                : token.colorText,
                                        }}
                                    >
                                        <Typography.Text
                                            style={{ color: 'inherit', fontSize: 14 }}
                                        >
                                            {msg.content}
                                        </Typography.Text>
                                    </div>
                                ) : null}
                                {!isOutgoing && isFirstInGroup ? (
                                    <Typography.Text
                                        type="secondary"
                                        style={{ fontSize: 12, marginTop: msg.content ? 4 : 0 }}
                                    >
                                        {msg.participant.displayName ||
                                            translate('support.help.chat.agentName')}
                                        {' • '}
                                        {translate('support.help.chat.agentRole')}
                                        {' • '}
                                        {formatRelativeTime(msg.createdAt, translate)}
                                    </Typography.Text>
                                ) : null}
                                {msg.attachments?.length ? (
                                    <div
                                        style={{
                                            maxWidth: '85%',
                                            marginTop: msg.content ? 4 : 0,
                                        }}
                                    >
                                        <AttachmentGallery
                                            attachments={msg.attachments}
                                            downloadLabel={translate('support.help.chat.download')}
                                            getAttachmentDownloadUrl={getAttachmentDownloadUrl}
                                        />
                                    </div>
                                ) : null}
                                {(isLastInGroup || isOutgoing) ? (
                                    <Flex align="center" gap={4} style={{ marginTop: 4 }}>
                                        {isLastInGroup && isOutgoing ? (
                                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                {dayjs(msg.createdAt).format('HH:mm')}
                                            </Typography.Text>
                                        ) : null}
                                        {isOutgoing ? (
                                            <span
                                                title={translate(
                                                    msg.isRead
                                                        ? 'support.help.chat.read'
                                                        : 'support.help.chat.delivered',
                                                )}
                                                aria-label={translate(
                                                    msg.isRead
                                                        ? 'support.help.chat.read'
                                                        : 'support.help.chat.delivered',
                                                )}
                                                style={{ display: 'inline-flex', alignItems: 'center' }}
                                            >
                                                {msg.isRead ? (
                                                    <span
                                                        style={{
                                                            position: 'relative',
                                                            display: 'inline-flex',
                                                            width: 16,
                                                        }}
                                                    >
                                                        <CheckOutlined
                                                            style={{
                                                                fontSize: 12,
                                                                color: token.colorPrimary,
                                                            }}
                                                        />
                                                        <CheckOutlined
                                                            style={{
                                                                fontSize: 12,
                                                                color: token.colorPrimary,
                                                                position: 'absolute',
                                                                left: 5,
                                                            }}
                                                        />
                                                    </span>
                                                ) : (
                                                    <CheckOutlined
                                                        style={{
                                                            fontSize: 12,
                                                            color: token.colorTextTertiary,
                                                        }}
                                                    />
                                                )}
                                            </span>
                                        ) : null}
                                    </Flex>
                                ) : null}
                            </Flex>
                        </React.Fragment>
                    );
                })
            )}

            {agentTyping ? (
                <Typography.Text
                    type="secondary"
                    aria-live="polite"
                    style={{ fontSize: 12, display: 'block', marginTop: 4 }}
                >
                    {translate('support.help.chat.agentTyping')}
                </Typography.Text>
            ) : null}

            {isError ? (
                <Typography.Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                    {translate('support.help.error')}
                </Typography.Text>
            ) : null}
        </div>
    );
};
